import React from "react";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const humanGeographyUnit4Notes: UnitNotes = {
  unitId: "unit-4",
  unitName: "Unit 4: Political Patterns and Processes",
  examWeight: "12–17%",
  focus: "Understanding how political power is organized across space, how boundaries are created and disputed, and how political systems shape human geography.",
  sections: [
    {
      id: "topic-4-1",
      title: "Topic 4.1: Introduction to Political Geography",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Structure of the Political Map</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The world is organized into political units. To understand geography, one must distinguish between the political entities involving people (nations) and the land/government (states).
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🏛️ State</h4>
                <p className="text-sm text-slate-700 mt-2">The fundamental unit of political geography (synonymous with "country").</p>
                <div className="bg-blue-100 p-2 rounded mt-2 space-y-1">
                  <p className="text-xs text-blue-800"><strong>1. Defined Boundary:</strong> Spatial limits clearly defined</p>
                  <p className="text-xs text-blue-800"><strong>2. Permanent Population:</strong> People live there effectively</p>
                  <p className="text-xs text-blue-800"><strong>3. Sovereignty:</strong> Ultimate authority over internal/external affairs</p>
                  <p className="text-xs text-blue-800"><strong>4. Recognition:</strong> Other states recognize it (e.g., UN)</p>
                </div>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> France, Nigeria, Brazil</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">👥 Nation</h4>
                <p className="text-sm text-slate-700 mt-2">A group of people who share a common cultural heritage and claim a particular space as their homeland.</p>
                <div className="bg-purple-100 p-2 rounded mt-2">
                  <p className="text-xs text-purple-800"><strong>Key:</strong> Nations are <em>people</em>. States are <em>political entities</em>.</p>
                </div>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> The Kurds, The Catalans, The Japanese people</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Variations of Political Entities</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🌍 Nation-State</h4>
                <p className="text-sm text-slate-700 mt-1">A state whose territory corresponds to that occupied by a particular ethnicity.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Ideal:</strong> 95%+ of population is one ethnicity</p>
                <p className="text-xs text-slate-600"><strong>Examples:</strong> Japan, Iceland, Denmark (rare in globalized world)</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🚫 Stateless Nation</h4>
                <p className="text-sm text-slate-700 mt-1">A nation of people without a state of their own. Often seek to become a nation-state.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> The Kurds (Turkey, Iraq, Iran, Syria), The Palestinians, The Basques (Spain/France)</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🏘️ Multinational State</h4>
                <p className="text-sm text-slate-700 mt-1">A country containing more than one ethnicity with traditions of self-determination.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Russia (largest), UK (English, Scottish, Welsh, Northern Irish), Canada (Anglophone/Francophone)</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🌐 Multistate Nation</h4>
                <p className="text-sm text-slate-700 mt-1">A nation that stretches across borders and across multiple states.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Koreans (North/South Korea), Germans (Austria/Switzerland)</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🏛️ Autonomous Region</h4>
                <p className="text-sm text-slate-700 mt-1">A defined area within a state with high degree of self-government and freedom from parent state.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Tibet (China), Hong Kong (China), Aland Islands (Finland)</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-2",
      title: "Topic 4.2: Political Processes",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Concepts of Power</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">👑 Sovereignty</h4>
                <p className="text-sm text-slate-700 mt-1">The right of a government to control and defend its territory and determine what happens within its borders.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">✊ Self-Determination</h4>
                <p className="text-sm text-slate-700 mt-1">The concept that ethnicities have the right to govern themselves. Driving force behind independence movements.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Historical Forces Shaping the Map</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🏴 Colonialism</h4>
                <p className="text-sm text-slate-700 mt-1">The rule by an autonomous power over a subordinate and alien people and place.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Impact:</strong> European powers drew boundaries for economic benefit, ignoring tribal/cultural lines</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">⚔️ Imperialism</h4>
                <p className="text-sm text-slate-700 mt-1">The drive by a state for colonization of territories and forcible imposition of its authority.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Berlin Conference (1884-1885):</strong> European powers divided Africa ("Scramble for Africa"), drawing superimposed boundaries that remain today</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🕊️ Independence Movements</h4>
                <p className="text-sm text-slate-700 mt-1">The effort by colonized people to win self-rule.</p>
                <div className="space-y-1 mt-2">
                  <p className="text-xs text-slate-600"><strong>Wave 1 (1700s-1800s):</strong> Americas (US, Latin America)</p>
                  <p className="text-xs text-slate-600"><strong>Wave 2 (1945-1970s):</strong> Africa and Asia (Post-WWII)</p>
                  <p className="text-xs text-slate-600"><strong>Wave 3 (1990s):</strong> Eastern Europe and Central Asia (USSR Collapse)</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-3",
      title: "Topic 4.3: Political Power and Territoriality",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Territoriality</h3>
            <KeyTerm 
              term="Territoriality" 
              definition="The connection of people, their culture, and their economic systems to the land. The attempt by an individual or group to affect, influence, or control people, phenomena, and relationships by delimiting and asserting control over a geographic area."
            />
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>Expression:</strong> We mark territory through boundaries, tariffs, border control, and defense.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Neocolonialism</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-bold text-purple-800">💰 Definition</h4>
              <p className="text-sm text-slate-700 mt-2">The practice of using capitalism, globalization, and cultural imperialism to influence a developing country instead of direct military control.</p>
              <p className="text-sm text-slate-700 mt-3"><strong>Mechanism:</strong> Developed countries (Core) exert economic power over developing countries (Periphery) through transnational corporations and debt.</p>
              <p className="text-xs text-slate-600 mt-2"><strong>Example:</strong> China's "Belt and Road Initiative" in Africa—building infrastructure in exchange for resource access and political influence</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Geopolitical Concepts</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">💥 Shatterbelts</h4>
                <p className="text-sm text-slate-700 mt-1">A region caught between stronger colliding external cultural-political forces, under persistent stress, and often fragmented by aggressive rivals.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Eastern Europe (Cold War), Southeast Asia (Vietnam War), Kashmir (India vs. Pakistan)</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🚢 Choke Points</h4>
                <p className="text-sm text-slate-700 mt-1">A strategic strait or canal which could be closed or blocked to stop sea traffic (especially oil).</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Strait of Hormuz (Persian Gulf oil), Strait of Malacca (Global trade), Panama Canal, Suez Canal</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-4",
      title: "Topic 4.4: Defining Political Boundaries",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Types of Boundaries</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Boundaries are thin vertical planes that cut through the rocks below and the airspace above.
            </p>

            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">🏚️ Relic Boundary</h4>
                <p className="text-sm text-slate-700 mt-1">A boundary that no longer functions as an international border but whose imprint remains on the cultural landscape.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> The Berlin Wall (East/West Germany), The Great Wall of China</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">⚔️ Superimposed Boundary</h4>
                <p className="text-sm text-slate-700 mt-1">A boundary imposed on an area by an outside or conquering power, ignoring existing cultural patterns.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Boundaries in Africa (Berlin Conference), DMZ between North and South Korea</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">📈 Subsequent Boundary</h4>
                <p className="text-sm text-slate-700 mt-1">A boundary established after settlement occurred. It developed with the evolution of the cultural landscape.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Border between Northern Ireland (UK) and Republic of Ireland; Borders in former Yugoslavia</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🏔️ Antecedent Boundary</h4>
                <p className="text-sm text-slate-700 mt-1">A boundary that existed before the cultural landscape emerged and stayed in place while people moved in.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> 49th parallel between US and Canada; Andes Mountains between Chile and Argentina</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">📐 Geometric Boundary</h4>
                <p className="text-sm text-slate-700 mt-1">A boundary created by using lines of latitude and longitude and their associated arcs.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> US/Canada border, Borders of Colorado/Wyoming, Many borders in North Africa</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🕌 Consequent Boundary</h4>
                <p className="text-sm text-slate-700 mt-1">A boundary line that coincides with some cultural divide, such as religion or language.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Partition of India (Hindu) and Pakistan (Muslim)</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-5",
      title: "Topic 4.5: The Function of Political Boundaries",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Lifecycle of a Boundary</h3>
            <div className="space-y-2">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h5 className="font-semibold text-slate-800">Definition</h5>
                  <p className="text-sm text-slate-600 mt-1">A treaty or legal document specifies the exact location</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h5 className="font-semibold text-slate-800">Delimitation</h5>
                  <p className="text-sm text-slate-600 mt-1">Cartographers put the boundary on the map</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h5 className="font-semibold text-slate-800">Demarcation</h5>
                  <p className="text-sm text-slate-600 mt-1">Physically marking the boundary on the ground (walls, posts, fences)</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h5 className="font-semibold text-slate-800">Administration</h5>
                  <p className="text-sm text-slate-600 mt-1">How the boundary is maintained, how goods/people cross it</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Boundaries and Disputes</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Boundaries often lead to conflict over resources or interpretation.
            </p>
            <div className="space-y-3">
              <KeyTerm term="Definitional Dispute" definition="Conflict over the language of the border agreement (e.g., 'the median line of a river'—rivers move)." />
              <KeyTerm term="Locational Dispute" definition="Conflict over where a boundary is placed on a map vs. the ground." />
              <KeyTerm term="Operational Dispute" definition="Conflict over the way a boundary should function (e.g., immigration policies)." />
              <KeyTerm term="Allocational Dispute" definition="Conflict over resources that may not be divided by the border, such as natural gas reserves or water sources." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Law of the Sea (UNCLOS)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The United Nations Convention on the Law of the Sea standardized territorial claims in 1982.
            </p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🌊 Territorial Sea (0–12 nm)</h4>
                <p className="text-sm text-slate-700 mt-1">Sovereign territory. Commercial vessels may pass, but non-commercial vessels may be challenged.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">📍 Contiguous Zone (12–24 nm)</h4>
                <p className="text-sm text-slate-700 mt-1">State can enforce laws regarding customs, immigration, and sanitation.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">💎 Exclusive Economic Zone (24–200 nm)</h4>
                <p className="text-sm text-slate-700 mt-1">State has sole right to exploit natural resources (fish, oil) but foreign vessels have freedom of passage.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🌍 High Seas (&gt;200 nm)</h4>
                <p className="text-sm text-slate-700 mt-1">International waters; open to all.</p>
              </div>
            </div>
            <ImportantNote>
              <strong>Median Line Principle:</strong> If two states are less than 400 nautical miles apart, the EEZ is divided evenly between them (e.g., Great Lakes, South China Sea).
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-6",
      title: "Topic 4.6: Internal Boundaries",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Voting Districts</h3>
            <div className="space-y-3">
              <KeyTerm term="Census" definition="A count of the population every 10 years (in the US) to determine demographic shifts." />
              <KeyTerm term="Reapportionment" definition="The process of reassigning representation based on population, after every census (e.g., Texas gains seats; New York loses seats)." />
              <KeyTerm term="Redistricting" definition="The redrawing of district boundaries within states to ensure roughly equal population in each district." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Gerrymandering</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 mb-4">
              <p className="text-sm text-slate-700">The practice of redistricting for political advantage (manipulating boundaries to favor one party).</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">📦 Packing</h4>
                <p className="text-sm text-slate-700 mt-1">Placing as many voters of one type in a single district to minimize the number of elections they can influence.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">💔 Cracking</h4>
                <p className="text-sm text-slate-700 mt-1">Spreading voters of a particular type among many districts to deny them a sufficiently large voting bloc in any particular district.</p>
              </div>
            </div>
            <ImportantNote>
              <strong>Consequence:</strong> Creates "safe districts" where politicians do not have to compromise; leads to polarization.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-7",
      title: "Topic 4.7: Forms of Governance",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Unitary States</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-800">📋 Definition</h4>
              <p className="text-sm text-slate-700 mt-2">A state where laws are administered uniformly by one central government. Power is concentrated in the capital.</p>
              <div className="space-y-2 mt-3">
                <p className="text-xs text-slate-600"><strong>✅ Pros:</strong> Efficiency, patriotism, less corruption at local levels</p>
                <p className="text-xs text-slate-600"><strong>❌ Cons:</strong> Disconnect with local needs, minorities may feel marginalized</p>
                <p className="text-xs text-slate-600"><strong>Examples:</strong> France, Japan, China, United Kingdom (though devolving)</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Federal States</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-bold text-green-800">🏛️ Definition</h4>
              <p className="text-sm text-slate-700 mt-2">A state where power is shared between a central government and regional/local governments (provinces, states).</p>
              <div className="space-y-2 mt-3">
                <p className="text-xs text-slate-600"><strong>✅ Pros:</strong> Enfranchises local minorities, handles large geography better</p>
                <p className="text-xs text-slate-600"><strong>❌ Cons:</strong> Inefficiency, overlapping authorities, inequality between regions</p>
                <p className="text-xs text-slate-600"><strong>Examples:</strong> United States, Canada, Russia, Germany, Nigeria, Brazil</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-8",
      title: "Topic 4.8: Defining Devolutionary Factors",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">What is Devolution?</h3>
            <KeyTerm 
              term="Devolution" 
              definition="The transfer of power from a central government to subnational (regional, local) authorities. It is a centrifugal force that can lead to the breakup of a state (balkanization) or the restructuring of a state into a federal system to keep it together."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Factors Leading to Devolution</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🏔️ Physical Geography</h4>
                <p className="text-sm text-slate-700 mt-1">Mountains, oceans, or distance can create isolation, leading to a desire for autonomy.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> The Basques (Pyrenees Mts.), Hawaii (distance), Indonesia (archipelago)</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">👥 Ethnic Separatism</h4>
                <p className="text-sm text-slate-700 mt-1">Ethnic groups with distinct languages/religions may want to separate from the majority.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Quebec (French in Canada), Catalonia (Spain), Flanders/Wallonia (Belgium)</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">💥 Terrorism/Ethnic Cleansing</h4>
                <p className="text-sm text-slate-700 mt-1">Violence can lead to a demand for separation or security.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Chechnya in Russia</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">💰 Economic & Social Problems</h4>
                <p className="text-sm text-slate-700 mt-1">If one region is richer than the other, they may feel they are subsidizing the poor region.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Northern Italy (Padania) vs. Southern Italy; Catalonia (richest part of Spain)</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🔄 Irredentism</h4>
                <p className="text-sm text-slate-700 mt-1">A movement to reunite a nation's homeland when part of it is spread into another state's borders.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Example:</strong> Russia annexing Crimea (Ukraine) because of the ethnic Russian population there</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-9",
      title: "Topic 4.9: Challenges to Sovereignty",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Devolution Outcomes</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">✅ Autonomous Regions</h4>
                <p className="text-sm text-slate-700 mt-1">The state grants power to the region to keep them happy.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Example:</strong> Nunavut in Canada</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">💔 Disintegration</h4>
                <p className="text-sm text-slate-700 mt-1">The state falls apart.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Yugoslavia (violent), Czechoslovakia (peaceful), USSR, Sudan/South Sudan</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Supranationalism</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <p className="text-sm text-slate-700">The practice of three or more states forming an organization for mutual benefit (economic, military, or political) to achieve shared goals. <strong>This challenges sovereignty because states must give up some control to the group.</strong></p>
            </div>

            <div className="space-y-3">
              <KeyTerm term="United Nations (UN)" definition="Global. Focus: Peacekeeping, humanitarian aid." />
              <KeyTerm term="NATO" definition="US, Canada, Europe. Focus: Military defense (Cold War origin)." />
              <KeyTerm term="European Union (EU)" definition="Europe. Focus: Economic integration (Euro currency, open borders/Schengen Zone)." />
              <KeyTerm term="ASEAN" definition="Southeast Asia. Focus: Economic growth and peace." />
              <KeyTerm term="Arctic Council" definition="High latitude countries. Focus: Climate change and sustainable development in the Arctic." />
              <KeyTerm term="African Union (AU)" definition="Africa. Focus: Economic and political integration." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Technology and Sovereignty</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">📱 Communication</h4>
                <p className="text-sm text-slate-700 mt-1">The internet and social media allow subnational groups to organize and seek support globally (e.g., Arab Spring).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🗳️ Democratization</h4>
                <p className="text-sm text-slate-700 mt-1">Technology brings outside information, challenging authoritarian regimes.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-10",
      title: "Topic 4.10: Consequences of Centrifugal and Centripetal Forces",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Centripetal Forces (Unify)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Forces that pull a country together.
            </p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🏛️ Political</h4>
                <p className="text-sm text-slate-700 mt-1">Strong national identity, external threats, charismatic leader</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">💰 Economic</h4>
                <p className="text-sm text-slate-700 mt-1">Infrastructure (roads/railroads), equitable economic development</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🎭 Cultural</h4>
                <p className="text-sm text-slate-700 mt-1">Common language, shared religion, national holidays, sports</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Centrifugal Forces (Divide)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Forces that push a country apart.
            </p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🏛️ Political</h4>
                <p className="text-sm text-slate-700 mt-1">Regionalism, lack of representation for minorities</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">💰 Economic</h4>
                <p className="text-sm text-slate-700 mt-1">Uneven development (rich vs. poor regions)</p>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-800">🎭 Cultural</h4>
                <p className="text-sm text-slate-700 mt-1">Linguistic/Religious differences, ethnic discrimination</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Consequences</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">💔 Failed States</h4>
                <p className="text-sm text-slate-700 mt-1">When centrifugal forces overwhelm the government, the state loses sovereignty and cannot provide basic services.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Somalia, Syria, Yemen</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">📊 Uneven Development</h4>
                <p className="text-sm text-slate-700 mt-1">If a government focuses infrastructure only on the "Core" (capital/rich areas) and ignores the "Periphery," it fuels centrifugal forces.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🔥 Ethnonationalism</h4>
                <p className="text-sm text-slate-700 mt-1">A strong feeling of belonging to a minority nation contained within a state; can lead to devolution or independence movements.</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default humanGeographyUnit4Notes;